import PropTypes from 'prop-types';
import React, {useState, useEffect} from 'react';
import {FmlxModal, FmlxLabel, FmlxTextBox} from 'fmlx-common-ui';
import withIntl from '../../lib/withIntl';
import {checkContainIllegalChars} from '../../lib/stringHelper';

function RenameProtocolModal(props) {
  const {intl, protocol, maxCharacters, protocolList, visible, renameClick, cancelClick, onCloseClick} = props;

  const protocolName = protocol?.name || '';
  const modalTitle =
    protocol?.type === 'folder'
      ? intl.formatMessage({id: 'explorer.modal.rename.title.folder'})
      : intl.formatMessage({id: 'explorer.modal.rename.title.protocol'});

  const [value, setValue] = useState(protocolName);
  const [warning, setWarning] = useState('');

  const checkMaxCharacterExceeded = (name) => name.length > maxCharacters;

  const checkProtocolNameExist = (name) =>
    name === protocolName
      ? false
      : protocolList.some((p) => p.type === protocol.type && p.name.trim().toLowerCase() === name.trim().toLowerCase());

  const validateValue = (newValue) => {
    if (!newValue?.trim()) {
      setWarning(intl.formatMessage({id: 'explorer.modal.rename.warning.required'}));
      return;
    }

    if (checkContainIllegalChars(newValue)) {
      setWarning(intl.formatMessage({id: 'explorer.modal.rename.warning.illegalCharacters'}));
      return;
    }

    if (checkMaxCharacterExceeded(newValue)) {
      setWarning(intl.formatMessage({id: 'explorer.modal.rename.warning.modalMaxCharacters'}));
      return;
    }

    if (checkProtocolNameExist(newValue)) {
      setWarning(
        intl.formatMessage(
          {id: 'explorer.modal.rename.warning.nameAlreadyExists'},
          {
            type: protocol?.type === 'folder' ? 'Folder' : 'Protocol',
          },
        ),
      );
      return;
    }

    setWarning('');
  };

  useEffect(() => {
    validateValue(protocolName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (input) => {
    const newValue = input.value;

    setValue(newValue);
    validateValue(newValue);
  };

  const renameClickHandler = () => {
    if (warning || value === protocolName) return;

    renameClick(value);
  };

  const cancelClickHandler = () => {
    cancelClick();
  };

  return (
    <FmlxModal
      open={visible}
      title={modalTitle}
      onCloseClick={onCloseClick}
      tertiaryButton={{show: false}}
      primaryButton={{
        label: intl.formatMessage({id: 'explorer.modal.rename.btnRename'}),
        disabled: !!warning || value === protocolName,
        show: true,
        onClick: renameClickHandler,
      }}
      secondaryButton={{
        label: intl.formatMessage({id: 'explorer.modal.rename.btnCancel'}),
        disabled: false,
        show: true,
        type: 'basic',
        variant: 'outline',
        onClick: cancelClickHandler,
      }}
    >
      <div className="name-text-box">
        <FmlxLabel label={intl.formatMessage({id: 'explorer.modal.rename.inputLabel'})} />
        <FmlxTextBox
          autoFocus
          value={value}
          onChange={onChange}
          inlineText={warning}
          error={!!warning}
          maxLength={maxCharacters}
          decoration="counter"
          onEnterKeyPressed={renameClickHandler}
        />
      </div>
    </FmlxModal>
  );
}

RenameProtocolModal.propTypes = {
  intl: PropTypes.object.isRequired,
  protocol: PropTypes.object.isRequired,
  maxCharacters: PropTypes.number,
  protocolList: PropTypes.array,
  visible: PropTypes.bool,
  renameClick: PropTypes.func,
  cancelClick: PropTypes.func,
  onCloseClick: PropTypes.func,
};

RenameProtocolModal.defaultProps = {
  maxCharacters: 25,
  protocolList: [],
  visible: false,
  renameClick: () => {},
  cancelClick: () => {},
  onCloseClick: () => {},
};

export default withIntl(RenameProtocolModal);
